import { Box } from '@mui/material';
import Table from '../../../core-components/theme/components/Table/Table';
import NoDataView from '../../../globalComponents/NoDataView/NoDataView';
import { DataLoggerTableColumns } from '../helpers/DataLoggerTableColumns';
import { useDataLoggerTable } from '../hooks/useDataLoggerTable';
import type { DataLogger } from '../hooks/useDataLoggerTable';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_RULES, allowedAtEveryPlant, anyPlant, useAccessCheck } from '../../../access';

interface DataLoggerTableProps {
  searchString: string;
  isPending?: boolean;
  onEditClick: (id: string) => void;
}

export default function DataLoggerTable({
  searchString,
  isPending,
  onEditClick,
}: DataLoggerTableProps) {
  const { t } = useTranslation();
  const {
    dataLoggers = [],
    tableContainerRef,
    lastElementRef
  } = useDataLoggerTable(searchString);
  const check = useAccessCheck();
  const canManageRow = useCallback(
    (row: DataLogger) => allowedAtEveryPlant(check, ANALYTICS_RULES.manageDataLogger, row.plantIds, anyPlant),
    [check],
  );
  const columns = useMemo(() => DataLoggerTableColumns(t, canManageRow, onEditClick), [t, canManageRow, onEditClick]);
  const hasNoData = !isPending && (!dataLoggers || dataLoggers.length === 0);

  return (
    <Box
      ref={tableContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: { xs: '76vh', md: '70vh' },
        opacity: isPending ? '0.3' : '1',
      }}
    >
      {hasNoData ? (
        <NoDataView message={t('common.noDataAvailable', 'No data available')} />
      ) : (
        <Table
          stickyHeader
          rows={dataLoggers}
          columns={columns}
          lastRowRef={lastElementRef}
        />
      )}
    </Box>
  );
} 